import saveAs from 'file-saver';
import React, { useMemo } from 'react';
import { BrandImage } from 'src/components/AtlasV2Components/AtlasV2SalesPageComponents/AtlasV2MarketShareGrid';
import { useAtlasV2MarketShareHooks } from 'src/components/AtlasV2Components/AtlasV2SalesPageComponents/useAtlasV2MarketShareHooks';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { EntityLink } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { safeDivide } from 'src/utils/app';
import { getPreviousYearWeekId, getYearForWeekId } from 'src/utils/dateUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useAppSelector,
  useCurrentAndLastYearAvailableTimePeriod,
  useMainTimePeriod,
  useMetricFormatter
} from 'src/utils/Hooks';

const METRIC_KEY = 'unitsSold_sum_value_';
interface RowsDefinition {
  cd: number;
  '1w': number;
  '4w': number;
  '13w': number;
  '26w': number;
  '52w': number;
  ly: number;
  ytd: number;
}

const totalRow = {
  cd: 1,
  '1w': 1,
  '4w': 1,
  '13w': 1,
  '26w': 1,
  '52w': 1,
  ly: 1,
  ytd: 1,
  name: 'totalRow',
  rank: 'totalRow'
};

const useFormattedColumnDefinitions = () => {
  const { id: mainTimePeriodId } = useMainTimePeriod();
  const formatMetric = useMetricFormatter();
  const COL_DEFS = useMemo(
    () => [
      {
        headerName: 'Rank',
        field: 'rank',
        valueFormatter: (value) => {
          if (value === 'otherBrandIds' || value === 'totalRow') {
            return <div style={{ fontSize: '14px' }}>-</div>;
          }

          return <div style={{ fontSize: '14px' }}>{value}</div>;
        }
      },
      // Brand Entity
      {
        headerName: 'Brands',
        field: 'name',
        valueFormatter: (value, row) => {
          if (value === 'otherBrandIds') {
            return <div style={{ fontSize: '14px' }}>Other</div>;
          }
          if (value === 'totalRow') {
            return <div style={{ fontSize: '14px' }}>Total</div>;
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BrandImage row={row} imageMode="base64" />
              <EntityLink id={row.brandId} entityType="brand">
                {row.name}
              </EntityLink>
            </div>
          );
        }
      },
      ...(mainTimePeriodId === 'cd'
        ? [
            {
              field: 'cd',
              headerName: 'Custom\n Date',
              valueFormatter: (value) => {
                return (
                  <div style={{ fontSize: '14px' }}>
                    {formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}
                  </div>
                );
              }
            }
          ]
        : []),
      {
        field: '1w',
        headerName: 'LAST\n WEEK',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
          );
        }
      },

      {
        field: '4w',
        headerName: 'LAST\n 4 WEEKS',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
          );
        }
      },

      {
        field: '13w',
        headerName: 'LAST\n 13 WEEK',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
          );
        }
      },

      {
        field: '26w',
        headerName: 'LAST\n 26 WEEK',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
          );
        }
      },

      {
        field: '52w',
        headerName: 'LAST\n 52 WEEK',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
          );
        }
      },
      {
        field: 'ly',
        headerName: 'LAST\n YEAR',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
          );
        }
      },
      {
        field: 'ytd',
        headerName: 'YTD',
        valueFormatter: (value) => {
          return (
            <div style={{ fontSize: '14px' }}>{formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
          );
        }
      }
    ],
    [formatMetric, mainTimePeriodId]
  );
  return COL_DEFS;
};

/**
 *
 * Market Share grid that will show up in category, brand, subcategory entity in units sold page
 */
const AtlasV2UnitsSoldMarketShareGrid = () => {
  const fields = useFormattedColumnDefinitions();
  const { availableMainTimePeriods } = useAppSelector((state) => state.mainTimePeriod);
  const { parsedData, isLoading } = useAtlasV2MarketShareHooks('unitsSold');
  const currentAndLastYearTimePeriod = useCurrentAndLastYearAvailableTimePeriod();
  const formatMetric = useMetricFormatter();
  const { startWeek: mainTimeStartWeek, endWeek: mainTimeEndWeek } = useMainTimePeriod();
  const { startWeek: compTimeStartWeek, endWeek: compTimeEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const { totalMarketValues, valuesByBrandMap, lastYearMarketValues, lastYearValuesByBrandMap } = parsedData;

  const customWeekIds = [
    `${METRIC_KEY}${mainTimeStartWeek}_${mainTimeEndWeek}`,
    `${METRIC_KEY}${compTimeStartWeek}_${compTimeEndWeek}`
  ];

  const weekIds: RowsDefinition = useMemo(() => {
    return availableMainTimePeriods.reduce((acc, curr) => {
      acc[curr.id] = [
        `${METRIC_KEY}${curr.startWeek}_${curr.endWeek}`,
        `${METRIC_KEY}${getPreviousYearWeekId(curr.startWeek)}_${getPreviousYearWeekId(curr.endWeek)}`
      ];
      return acc;
    }, {} as RowsDefinition);
  }, [availableMainTimePeriods]);

  const rows = Array.from(valuesByBrandMap)
    .map(([_, metadata], index) => {
      return {
        rank: index + 1,
        brandId: metadata.brandId,
        name: metadata.name,
        cd: safeDivide(metadata[customWeekIds[0]], totalMarketValues[customWeekIds[0]]),
        '1w': safeDivide(metadata[weekIds['1w'][0]], totalMarketValues[weekIds['1w'][0]]),
        '4w': safeDivide(metadata[weekIds['4w'][0]], totalMarketValues[weekIds['4w'][0]]),
        '13w': safeDivide(metadata[weekIds['13w'][0]], totalMarketValues[weekIds['13w'][0]]),
        '26w': safeDivide(metadata[weekIds['26w'][0]], totalMarketValues[weekIds['26w'][0]]),
        '52w': safeDivide(metadata[weekIds['52w'][0]], totalMarketValues[weekIds['52w'][0]]),
        ly: safeDivide(metadata[weekIds.ly[0]], totalMarketValues[weekIds.ly[0]]),
        ytd: safeDivide(metadata[weekIds.ytd[0]], totalMarketValues[weekIds.ytd[0]])
      };
    })
    .filter((row) => row.rank < 11)
    .sort((aRow, bRow) => bRow?.cd - aRow?.cd);

  const salesSumRow = rows.reduce(
    (acc, curr) => {
      acc['1w'] -= curr['1w'];
      acc['4w'] -= curr['4w'];
      acc['13w'] -= curr['13w'];
      acc['26w'] -= curr['26w'];
      acc['52w'] -= curr['52w'];
      acc.cd -= curr.cd;
      acc.ly -= curr.ly;
      acc.ytd -= curr.ytd;
      return acc;
    },
    {
      '1w': 1,
      '4w': 1,
      '13w': 1,
      '26w': 1,
      '52w': 1,
      cd: 1,
      ly: 1,
      ytd: 1,
      name: 'otherBrandIds',
      rank: 'otherBrandIds'
    }
  );

  const convertSeriesToDelimitedData = () => {
    try {
      const header = ['Date Range ->'];
      // fill the header row
      currentAndLastYearTimePeriod.forEach((tpSet) => {
        const { startWeek, endWeek } = tpSet[0];
        const { prevYearStartWeek, prevYearEndWeek } = tpSet[1];

        header.push(
          `${getYearForWeekId(+endWeek)} (${startWeek} - ${endWeek})`, // units sold
          '', // share=
          `${getYearForWeekId(+prevYearEndWeek)} (${prevYearStartWeek} - ${prevYearEndWeek})`, // last year units sold
          '', // last year share
          '', // YoY units sold
          '' // YoY share
        );
      });

      const subHeader = ['Brand'];
      currentAndLastYearTimePeriod.forEach((tpSet) => {
        const { shortDisplayName } = tpSet[0];
        subHeader.push(
          `${shortDisplayName} Units Sold`,
          `${shortDisplayName} Share`,
          `${shortDisplayName} LY Units Sold`,
          `${shortDisplayName} LY Share`,
          `${shortDisplayName} YoY Units Sold (%)`,
          `${shortDisplayName} YoY Share (%)`
        );
      });

      const calculateMetrics = (brand, periodKey) => {
        const currentUnitsSold = brand[weekIds[periodKey][0]];
        const lastYearUnitsSold = lastYearValuesByBrandMap.get(brand.brandId)[weekIds[periodKey][1]];
        const currentShare = safeDivide(currentUnitsSold, totalMarketValues[weekIds[periodKey][0]]);
        const lastYearShare = safeDivide(lastYearUnitsSold, lastYearMarketValues[weekIds[periodKey][1]]);
        const yoyUnitsSold = safeDivide(currentUnitsSold, lastYearUnitsSold) - 1;
        const yoyShare = safeDivide(currentShare, lastYearShare) - 1;

        return [
          currentUnitsSold,
          formatMetric(currentShare, METRICTYPE.PERCENT, { decimalPlaces: 2 }),
          lastYearUnitsSold,
          formatMetric(lastYearShare, METRICTYPE.PERCENT, { decimalPlaces: 2 }),
          yoyUnitsSold,
          formatMetric(yoyShare, METRICTYPE.PERCENT, { decimalPlaces: 2 })
        ];
      };

      const brandRows = Array.from(valuesByBrandMap.values()).map((brand) => {
        const brandRow = [brand.name];
        const periods = ['1w', '4w', '13w', '26w', '52w', 'ly', 'ytd'];

        periods.forEach((periodKey) => {
          brandRow.push(...calculateMetrics(brand, periodKey));
        });

        return brandRow;
      });

      const csvArray = [header, [], subHeader, ...brandRows].map((row) => row.join(',')).join('\n');
      const blob = new Blob([csvArray], { type: 'text/plain;charset=utf-8' });
      const fileName = 'market_share_unitsSold_data.tsv';
      saveAs(blob, `${fileName}.csv`);
    } catch (error) {
      console.error('Fail to generate - market share grid ', error);
    }

    return Promise.resolve();
  };

  return (
    <>
      <BeaconChartWithLegend
        showTitleSummary
        title="Market Share Grid of Units Sold"
        convertSeriesToDelimitedData={() => convertSeriesToDelimitedData()}
      >
        <EntityTable
          columnDefs={fields}
          shouldModifyColumnDefs={false}
          showPagination={false}
          isLoading={isLoading}
          rowData={[...rows, salesSumRow, totalRow]}
          skeletonRows={13}
        />
      </BeaconChartWithLegend>
    </>
  );
};

export default AtlasV2UnitsSoldMarketShareGrid;
